import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiUrl } from '../lib/api';
import {
  initializeBrowserImpersonation,
  readBrowserImpersonation,
  type BrowserImpersonationState,
} from '@command-center/auth-sdk/browser-impersonation';
import { decideAuthEvent, isAuthoritativeDenial } from '@command-center/auth-sdk/session-events';
import {
  authedFetch,
  getSupabaseBrowserClient,
  getSupabasePublishableKey,
  getSupabaseUrl,
  requestPasswordReset,
  signInWithPassword,
  signOut as supabaseSignOut,
  updatePassword,
  type SupabaseSession,
  type SupabaseUser,
} from './supabase-browser';

type AccessState = 'checking' | 'allowed' | 'denied';

export interface AuthContextValue {
  isLoading: boolean;
  isConfigured: boolean;
  session: SupabaseSession | null;
  user: SupabaseUser | null;
  impersonation: BrowserImpersonationState | null;
  accessState: AccessState;
  accessError?: string;
  isPasswordRecovery: boolean;
  signIn: (email: string, password: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  signOut: () => Promise<void>;
  requestPasswordReset: typeof requestPasswordReset;
  updatePassword: typeof updatePassword;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const IMPERSONATION_IDENTITY = 'impersonation';

interface AuthMeBody {
  authenticated?: boolean;
  error?: { message?: string };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<SupabaseSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessState, setAccessState] = useState<AccessState>('checking');
  const [accessError, setAccessError] = useState<string>();
  const [impersonation, setImpersonation] = useState<BrowserImpersonationState | null>(null);
  const [isPasswordRecovery, setPasswordRecovery] = useState(
    window.location.pathname === '/auth/reset',
  );
  const isConfigured = useMemo(
    () => Boolean(getSupabaseUrl() && getSupabasePublishableKey()),
    [],
  );

  /** The last user id any auth event reported, approved or not. */
  const knownUserId = useRef<string | null>(null);
  /** The user id currently holding an `allowed` decision. */
  const allowedUserId = useRef<string | null>(null);
  /** Monotonic verification id. Only the newest run may write state. */
  const latestRun = useRef(0);
  /** An in-flight verification, so one identity never has two of them. */
  const inFlight = useRef<{ identity: string; promise: Promise<void> } | null>(null);

  /**
   * Confirm this session's access against the Worker.
   *
   * Two rules make this safe to call as often as Supabase emits events:
   *
   * 1. It only blanks the portal when the identity being checked is not the one
   *    already approved. Re-confirming an approved user is invisible.
   * 2. Concurrent calls for the same identity share one request, and a slower
   *    older run can never overwrite a newer one's result.
   */
  const verifyAccess = useCallback(async (
    nextSession: SupabaseSession | null,
    options: { background?: boolean } = {},
  ): Promise<void> => {
    const identity = nextSession?.user.id
      ?? (readBrowserImpersonation() ? IMPERSONATION_IDENTITY : null);
    if (!identity) {
      latestRun.current += 1;
      inFlight.current = null;
      allowedUserId.current = null;
      setAccessState('checking');
      setAccessError(undefined);
      return;
    }

    // Revalidation of an identity the portal has already admitted. Nothing here
    // may take the workspace away except an authoritative answer.
    const isRevalidation = allowedUserId.current === identity;
    if (!options.background && !isRevalidation) {
      setAccessState('checking');
      setAccessError(undefined);
    }

    const pending = inFlight.current;
    if (pending?.identity === identity) return pending.promise;

    const run = (latestRun.current += 1);
    const commit = (apply: () => void) => {
      // A newer verification has started (or this identity was signed out).
      // Its answer wins regardless of which request returned first.
      if (run !== latestRun.current) return;
      apply();
    };

    const promise = (async () => {
      let response: Response | undefined;
      let body: AuthMeBody | null = null;
      try {
        response = await authedFetch(apiUrl('/api/auth/me'), {
          headers: { Accept: 'application/json' },
        });
        body = await response.json().catch(() => null) as AuthMeBody | null;
      } catch {
        response = undefined;
      }

      if (response?.ok && body?.authenticated) {
        commit(() => {
          allowedUserId.current = identity;
          setAccessState('allowed');
          setAccessError(undefined);
        });
        return;
      }

      // A successful response saying "no access" is a decision. A transport
      // failure or a 5xx is not — during revalidation the portal keeps running
      // on the authorization it already has, and the API remains the boundary
      // that actually enforces it.
      if (isRevalidation && !isAuthoritativeDenial(response?.status)) return;

      commit(() => {
        allowedUserId.current = null;
        setAccessState('denied');
        setAccessError(
          response === undefined
            ? 'The Cascade portal service is temporarily unavailable. Please try again.'
            : body?.error?.message ?? 'Your account does not have access to the Cascade portal.',
        );
      });
    })().finally(() => {
      if (inFlight.current?.promise === promise) inFlight.current = null;
    });

    inFlight.current = { identity, promise };
    return promise;
  }, []);

  /** Drop everything scoped to the person who was signed in. */
  const clearUserScopedState = useCallback(() => {
    latestRun.current += 1;
    inFlight.current = null;
    allowedUserId.current = null;
    knownUserId.current = null;
    queryClient.clear();
    setAccessState('checking');
    setAccessError(undefined);
  }, [queryClient]);

  useEffect(() => {
    let disposed = false;
    let unsubscribe: (() => void) | undefined;
    void initializeBrowserImpersonation(apiUrl('')).then(async (supportSession) => {
      if (disposed) return;
      setImpersonation(supportSession);
      if (supportSession) {
        setIsLoading(false);
        await verifyAccess(null);
        return;
      }
      const supabase = getSupabaseBrowserClient();
      if (!supabase) {
        setIsLoading(false);
        setAccessState('denied');
        return;
      }
      const { data: current } = await supabase.auth.getSession();
      if (disposed) return;
      knownUserId.current = current.session?.user.id ?? null;
      setSession(current.session);
      setIsLoading(false);
      // Deliberately not awaited: `onAuthStateChange` fires INITIAL_SESSION
      // synchronously on subscribe and is deduplicated against this same
      // request, so the bootstrap stays a single round trip either way.
      void verifyAccess(current.session);

      const { data } = supabase.auth.onAuthStateChange((event, nextSession) => {
        const nextUserId = nextSession?.user.id ?? null;
        const decision = decideAuthEvent({
          event,
          nextUserId,
          knownUserId: knownUserId.current,
          allowedUserId: allowedUserId.current,
        });
        setSession(nextSession);
        if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);

        switch (decision.kind) {
          case 'sign-out':
            clearUserScopedState();
            setPasswordRecovery(false);
            return;
          case 'switch-identity':
            clearUserScopedState();
            knownUserId.current = nextUserId;
            void verifyAccess(nextSession);
            return;
          case 'verify':
            knownUserId.current = nextUserId;
            void verifyAccess(nextSession, { background: !decision.blocking });
            return;
          case 'ignore':
            knownUserId.current = nextUserId ?? knownUserId.current;
        }
      });
      unsubscribe = () => data.subscription.unsubscribe();
    }).catch((error: unknown) => {
      if (disposed) return;
      setIsLoading(false);
      setAccessState('denied');
      setAccessError(error instanceof Error ? error.message : 'The support session could not be opened.');
    });
    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, [clearUserScopedState, verifyAccess]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await signInWithPassword(email, password);
    if (result.ok) {
      setSession(result.session);
      knownUserId.current = result.session.user.id;
      // Supabase emits SIGNED_IN for this same sign-in. Both paths land on the
      // one in-flight verification for this identity rather than racing.
      await verifyAccess(result.session);
    }
    return result.ok ? { ok: true as const } : result;
  }, [verifyAccess]);

  const signOut = useCallback(async () => {
    await supabaseSignOut();
    clearUserScopedState();
    setSession(null);
  }, [clearUserScopedState]);

  const value = useMemo<AuthContextValue>(() => ({
    isLoading,
    isConfigured,
    session,
    user: session?.user ?? null,
    impersonation,
    accessState,
    accessError,
    isPasswordRecovery,
    signIn,
    signOut,
    requestPasswordReset,
    updatePassword,
  }), [
    accessError,
    accessState,
    isConfigured,
    isLoading,
    isPasswordRecovery,
    impersonation,
    session,
    signIn,
    signOut,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within an AuthProvider.');
  return value;
}
